import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { usePaymentCards } from "@/hooks/use-payment-cards";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Plus, CreditCard, Check, Trash2, Pencil, Star, CalendarIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from "@/components/ui/alert-dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { PaymentCard, Application } from "@shared/schema";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { insertPaymentCardSchema } from "@shared/schema";

// Extend the schema with client-side validation
const cardFormSchema = insertPaymentCardSchema.extend({
  // Ensure last four digits are exactly 4 digits
  lastFourDigits: z.string().length(4, "Must be exactly 4 digits"),
  // Ensure expiry month is between 1-12
  expiryMonth: z.number().min(1).max(12),
  // Ensure expiry year is at least current year
  expiryYear: z.number().min(new Date().getFullYear()),
});

type CardFormValues = z.infer<typeof cardFormSchema>;

function getCardLogoColor(provider: string): string {
  const colors: { [key: string]: string } = {
    visa: "text-blue-600",
    mastercard: "text-red-500",
    amex: "text-blue-800",
    discover: "text-orange-500",
    default: "text-gray-600",
  };
  return colors[provider.toLowerCase()] || colors.default;
}

function formatExpiryDate(month: number, year: number): string {
  return `${month.toString().padStart(2, "0")}/${year.toString().slice(-2)}`;
}

function CardItem({ card, onSelect, selected }: { 
  card: PaymentCard; 
  onSelect: (id: number) => void;
  selected: boolean;
}) {
  const { deleteCardMutation, setDefaultCardMutation } = usePaymentCards();
  
  return (
    <Card
      className={`p-4 mb-4 transition-all ${
        selected ? "ring-2 ring-primary" : ""
      } cursor-pointer hover:shadow-md`}
      onClick={() => onSelect(card.id)}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`text-2xl ${getCardLogoColor(card.cardProvider)}`}>
            <CreditCard />
          </div>
          <div>
            <div className="font-semibold">{card.cardName}</div>
            <div className="text-sm text-muted-foreground">
              {card.cardProvider} •••• {card.lastFourDigits}
            </div>
            <div className="text-xs text-muted-foreground">
              Expires: {formatExpiryDate(card.expiryMonth, card.expiryYear)}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          {card.isDefault && <Badge className="mb-1">Default</Badge>}
          
          {/* Action buttons - always visible */}
          <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
            {/* Edit button */}
            <EditCardDialog card={card} />
            
            {/* Set default button */}
            {!card.isDefault ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  setDefaultCardMutation.mutate(card.id);
                }}
                title="Set as default"
              >
                <Star size={16} />
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="icon"
                disabled={true}
                title="This is already the default card"
              >
                <Star className="fill-yellow-400 text-yellow-400" size={16} />
              </Button>
            )}
            
            {/* Delete button */}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="icon" onClick={(e) => e.stopPropagation()}>
                  <Trash2 size={16} />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Payment Card</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete this payment card? This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => deleteCardMutation.mutate(card.id)}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>
    </Card>
  );
}

function AddCardDialog() {
  const [open, setOpen] = useState(false);
  const { createCardMutation } = usePaymentCards();
  
  const form = useForm<CardFormValues>({
    resolver: zodResolver(cardFormSchema),
    defaultValues: {
      cardName: "",
      cardType: "credit",
      cardProvider: "",
      lastFourDigits: "",
      expiryMonth: 1,
      expiryYear: new Date().getFullYear(),
      cardholderName: "",
      status: "active",
      isDefault: false,
      bankName: "",
      billingAddress: "",
    },
  });
  
  function onSubmit(values: CardFormValues) {
    createCardMutation.mutate(values, {
      onSuccess: () => {
        setOpen(false);
        form.reset();
      },
    });
  }
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full mb-4">
          <Plus size={16} className="mr-2" /> Add Payment Card
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Payment Card</DialogTitle>
          <DialogDescription>
            Enter the payment card details below.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="cardName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Card Nickname</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Company Amex" {...field} />
                  </FormControl>
                  <FormDescription>
                    A name to help you identify this card
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="cardType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Card Type</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select card type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="credit">Credit Card</SelectItem>
                        <SelectItem value="debit">Debit Card</SelectItem>
                        <SelectItem value="corporate">Corporate Card</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="cardProvider"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Card Provider</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select provider" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Visa">Visa</SelectItem>
                        <SelectItem value="Mastercard">Mastercard</SelectItem>
                        <SelectItem value="Amex">American Express</SelectItem>
                        <SelectItem value="Discover">Discover</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="lastFourDigits"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last 4 Digits</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="1234"
                      maxLength={4}
                      pattern="[0-9]{4}"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    For identification purposes only
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="cardholderName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cardholder Name</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="expiryMonth"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Expiry Month</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        min={1} 
                        max={12} 
                        placeholder="MM"
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value))} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="expiryYear"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Expiry Year</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        min={new Date().getFullYear()} 
                        placeholder="YYYY"
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value))} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="bankName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bank Name (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="Bank of America" value={field.value || ""} onChange={field.onChange} onBlur={field.onBlur} name={field.name} ref={field.ref} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="billingAddress"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Billing Address (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="123 Main St, City, State" value={field.value || ""} onChange={field.onChange} onBlur={field.onBlur} name={field.name} ref={field.ref} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="isDefault"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <input
                      type="checkbox"
                      checked={field.value || false}
                      onChange={field.onChange}
                      className="h-4 w-4"
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Set as default payment method</FormLabel>
                    <FormDescription>
                      This card will be used for new subscriptions by default
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />
            
            <DialogFooter>
              <Button type="submit" disabled={createCardMutation.isPending}>
                {createCardMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Add Card
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function EditCardDialog({ card }: { card: PaymentCard }) {
  const [open, setOpen] = useState(false);
  const { updateCardMutation } = usePaymentCards();
  
  const form = useForm<CardFormValues>({
    resolver: zodResolver(cardFormSchema),
    defaultValues: {
      cardName: card.cardName,
      cardType: card.cardType,
      cardProvider: card.cardProvider,
      lastFourDigits: card.lastFourDigits,
      expiryMonth: card.expiryMonth,
      expiryYear: card.expiryYear,
      cardholderName: card.cardholderName,
      status: card.status,
      isDefault: card.isDefault,
      bankName: card.bankName || "",
      billingAddress: card.billingAddress || "",
    },
  });
  
  function onSubmit(values: CardFormValues) {
    updateCardMutation.mutate({ 
      id: card.id, 
      card: values 
    }, {
      onSuccess: () => {
        setOpen(false);
        form.reset();
      },
    });
  }
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={(e) => e.stopPropagation()}
          title="Edit card"
        >
          <Pencil size={16} />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Payment Card</DialogTitle>
          <DialogDescription>
            Update the payment card details below.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="cardName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Card Nickname</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Company Amex" {...field} />
                  </FormControl>
                  <FormDescription>
                    A name to help you identify this card
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="cardType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Card Type</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select card type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="credit">Credit Card</SelectItem>
                        <SelectItem value="debit">Debit Card</SelectItem>
                        <SelectItem value="corporate">Corporate Card</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="cardProvider"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Card Provider</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select provider" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Visa">Visa</SelectItem>
                        <SelectItem value="Mastercard">Mastercard</SelectItem>
                        <SelectItem value="Amex">American Express</SelectItem>
                        <SelectItem value="Discover">Discover</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="lastFourDigits"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last 4 Digits</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="1234"
                      maxLength={4}
                      pattern="[0-9]{4}"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    For identification purposes only
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="cardholderName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cardholder Name</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="expiryMonth"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Expiry Month</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        min={1} 
                        max={12} 
                        placeholder="MM"
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value))} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="expiryYear"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Expiry Year</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        min={new Date().getFullYear()} 
                        placeholder="YYYY"
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value))} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="bankName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bank Name (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="Bank of America" value={field.value || ""} onChange={field.onChange} onBlur={field.onBlur} name={field.name} ref={field.ref} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="billingAddress"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Billing Address (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="123 Main St, City, State" value={field.value || ""} onChange={field.onChange} onBlur={field.onBlur} name={field.name} ref={field.ref} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="isDefault"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <input
                      type="checkbox"
                      checked={field.value || false}
                      onChange={field.onChange}
                      className="h-4 w-4"
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Set as default payment method</FormLabel>
                    <FormDescription>
                      This card will be used for new subscriptions by default
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />
            
            <DialogFooter>
              <Button type="submit" disabled={updateCardMutation.isPending}>
                {updateCardMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function CardDetail({ cardId }: { cardId: number }) {
  const { 
    selectedCard, 
    cardLicenses, 
    monthlySpend, 
    isLoading, 
    error,
    setSelectedCardId,
    setDefaultCardMutation
  } = usePaymentCards();
  
  const { data: applications = [] } = useQuery<Application[]>({
    queryKey: ['/api/applications'],
  });
  
  // Set the selected card ID when the component renders with a new card ID
  useEffect(() => {
    // Only update if necessary (prevents potential infinite loop)
    if (cardId && (!selectedCard || selectedCard.id !== cardId)) {
      setSelectedCardId(cardId);
    }
  }, [cardId, selectedCard, setSelectedCardId]);
  
  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Error</h2>
          <p className="text-muted-foreground mb-4">Could not load card details</p>
          <Button onClick={() => setSelectedCardId(cardId)}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }
  
  // No card selected state
  if (!selectedCard) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">No Card Selected</h2>
          <p className="text-muted-foreground mb-4">Please select a card to view details</p>
        </div>
      </div>
    );
  }
  
  // Helper function to get application name
  const getApplicationName = (applicationId: number) => {
    const app = applications.find((app: Application) => app.id === applicationId);
    return app ? app.name : 'Unknown';
  };
  
  // Calculate upcoming payments
  const getUpcomingPayments = () => {
    if (!cardLicenses || cardLicenses.length === 0) return [];
    
    const now = new Date();
    const sixMonthsLater = new Date();
    sixMonthsLater.setMonth(now.getMonth() + 6);
    
    // Sort licenses by renewal date
    const sortedLicenses = [...cardLicenses].sort((a, b) => 
      new Date(a.renewalDate).getTime() - new Date(b.renewalDate).getTime()
    );
    
    // Get only future renewals within next 6 months
    return sortedLicenses.filter(license => 
      new Date(license.renewalDate) > now && 
      new Date(license.renewalDate) < sixMonthsLater
    );
  };
  
  const upcomingPayments = getUpcomingPayments();
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">{selectedCard.cardName}</h2>
        <Badge variant={selectedCard.status === "active" ? "default" : "secondary"}>
          {selectedCard.status}
        </Badge>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-4">
          <h3 className="font-medium mb-2">Card Details</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Type:</span>
              <span className="font-medium">{selectedCard.cardType}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Provider:</span>
              <span className="font-medium">{selectedCard.cardProvider}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Last 4 Digits:</span>
              <span className="font-medium">{selectedCard.lastFourDigits}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Cardholder:</span>
              <span className="font-medium">{selectedCard.cardholderName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Expiry Date:</span>
              <span className="font-medium">
                {formatExpiryDate(selectedCard.expiryMonth, selectedCard.expiryYear)}
              </span>
            </div>
            {selectedCard.bankName && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Bank:</span>
                <span className="font-medium">{selectedCard.bankName}</span>
              </div>
            )}
          </div>
        </Card>
        
        <Card className="p-4">
          <h3 className="font-medium mb-2">Usage Statistics</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Monthly Spend:</span>
              <span className="font-medium">
                ${monthlySpend.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Linked Licenses:</span>
              <span className="font-medium">{cardLicenses.length}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Default Card:</span>
              <div className="flex items-center gap-2">
                <span className="font-medium">
                  {selectedCard.isDefault ? "Yes" : "No"}
                </span>
                {!selectedCard.isDefault && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setDefaultCardMutation.mutate(selectedCard.id)}
                    className="px-2 h-7 text-xs"
                  >
                    Set as Default
                  </Button>
                )}
              </div>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Added On:</span>
              <span className="font-medium">
                {new Date(selectedCard.createdAt).toLocaleDateString()}
              </span>
            </div>
          </div>
        </Card>
      </div>
      
      {/* Payment Forecast Section */}
      <div>
        <h3 className="text-lg font-medium mb-3 flex items-center">
          <CalendarIcon className="mr-2 h-5 w-5" />
          Upcoming Payments
        </h3>
        {upcomingPayments.length === 0 ? (
          <Card className="p-6 text-center text-muted-foreground">
            No upcoming payments in the next 6 months.
          </Card>
        ) : (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Application</TableHead>
                  <TableHead>License</TableHead>
                  <TableHead>Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {upcomingPayments.map((license) => (
                  <TableRow key={`upcoming-${license.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                        {new Date(license.renewalDate).toLocaleDateString()}
                      </div>
                    </TableCell>
                    <TableCell>{getApplicationName(license.applicationId)}</TableCell>
                    <TableCell className="font-medium">{license.name}</TableCell>
                    <TableCell>${license.cost.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>
      
      {/* Linked Subscriptions Section */}
      <div>
        <h3 className="text-lg font-medium mb-3 flex items-center">
          <CreditCard className="mr-2 h-5 w-5" />
          Linked Subscriptions
        </h3>
        {cardLicenses.length === 0 ? (
          <Card className="p-6 text-center text-muted-foreground">
            No subscriptions linked to this payment card yet.
          </Card>
        ) : (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>License</TableHead>
                  <TableHead>Application</TableHead>
                  <TableHead>Cost</TableHead>
                  <TableHead>Billing Cycle</TableHead>
                  <TableHead>Next Renewal</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cardLicenses.map((license) => (
                  <TableRow key={license.id}>
                    <TableCell className="font-medium">{license.name}</TableCell>
                    <TableCell>{getApplicationName(license.applicationId)}</TableCell>
                    <TableCell>${license.cost ? license.cost.toFixed(2) : '0.00'}</TableCell>
                    <TableCell className="capitalize">{license.billingCycle}</TableCell>
                    <TableCell>
                      {new Date(license.renewalDate).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>
    </div>
  );
}

export default function PaymentCardsPage() {
  const { cards, selectedCardId, setSelectedCardId, isLoading, error } = usePaymentCards();
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Error Loading Payment Cards</h2>
          <p className="text-muted-foreground mb-4">{(error as Error).message}</p>
          <Button onClick={() => window.location.reload()}>Try Again</Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container py-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Payment Cards</h1>
        <p className="text-muted-foreground">
          Manage your payment methods for all your software subscriptions.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="space-y-4">
          <AddCardDialog />
          
          <div className="space-y-2">
            {cards.length === 0 ? (
              <Card className="p-6 text-center">
                <p className="text-muted-foreground mb-4">
                  No payment cards added yet.
                </p>
                <p className="text-sm text-muted-foreground">
                  Add your first payment card to start managing your software subscriptions.
                </p>
              </Card>
            ) : (
              cards.map((card) => (
                <CardItem
                  key={card.id}
                  card={card}
                  onSelect={setSelectedCardId}
                  selected={card.id === selectedCardId}
                />
              ))
            )}
          </div>
        </div>
        
        <div className="col-span-2">
          {selectedCardId ? (
            <CardDetail cardId={selectedCardId} />
          ) : (
            <Card className="p-6 h-full flex items-center justify-center">
              <div className="text-center max-w-md">
                <CreditCard className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-medium mb-2">Card Details</h3>
                <p className="text-muted-foreground mb-4">
                  Select a payment card from the list to view its details and manage
                  related subscriptions.
                </p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}